import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Success } from '../assets/icons';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  useGlobalModalContext,
} from '../components/_ui';
import { IconSize } from '../constant/IconSize.constant';
import { RoutePath } from '../routes';
import { useCompany } from './useCompany';
import { useLicenseRenewalResultFromStorage } from './useLicenseRenewalResultFromStorage';
import { useNavigateWithSearchParam } from './useNavigateWithSearchParam';

export const useShowLicenseSelfServeRenewalResultModal = () => {
  const { t } = useTranslation();
  const { companyId } = useCompany();
  const currentLoation = useLocation();
  const [searchParams] = useSearchParams();
  const { renewalId, licenses } = useLicenseRenewalResultFromStorage();
  const { showModal } = useGlobalModalContext();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();
  const [showRenewalResultModal, setShowRenewalResultModal] = useState<boolean>(false);

  const isRenewalResultRoute = currentLoation.pathname.endsWith(
    RoutePath.LICENSE_SELF_RENEWAL_SUCCESS,
  );
  const renewalIdFromUrl = isRenewalResultRoute ? searchParams.get('renewalId') : null;

  const navigateToLicensePage = () => {
    navigateWithCompanyId(`/${RoutePath.LICENSES}`, companyId, {
      replace: true,
    });
  };

  const renderModalBody = () => {
    return (
      <div className='items-center justify-center w-full'>
        <div className='flex items-center justify-center w-full'>
          <Icon
            size={IconSize.SIZE_64x64}
            src={Success}
            color={ColorType.POSITIVE2}
            className='flex items-center justify-center w-full'
          />
        </div>
        <Label
          text={t('license_self_renew_success_details', {
            count: licenses.length,
          })}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          className='mt-4 text-center'
        />
        <Label
          text={t('license_self_renew_success_email_confirm')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          className='pt-2 pb-4 text-center'
        />
        <Button
          label={t('close')}
          type={ButtonType.PRIMARY}
          size={ButtonSize.SMALL_FULL}
          onClick={navigateToLicensePage}
          className='mt-2 mb-5'
        />
      </div>
    );
  };

  const displayRenewalResult = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('license_self_renew_success'),
      width: '540px',
      height: 'max-content',
      onRenderBody: renderModalBody,
      onIconClick: () => navigateToLicensePage(),
      shouldCloseOnOverlayClick: false,
    });
  };

  useEffect(() => {
    setShowRenewalResultModal(
      isRenewalResultRoute && renewalIdFromUrl != null && renewalIdFromUrl === renewalId,
    );
  }, [isRenewalResultRoute, renewalIdFromUrl, renewalId]);

  return { showRenewalResultModal, displayRenewalResult };
};
